
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdVisibility,
    MdSearch
} from 'react-icons/md';
import './ProjectCategory.css'; // Reusing styles
import '../pages/Dashboard.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import {
    GET_ALL_UNITS_API,
    DELETE_UNIT_API
} from '../config/API';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { useUser } from '../context/UserContext';

const UnitMaster = () => {
    const navigate = useNavigate();
    const { hasPermission } = useUser();
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const fetchUnits = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_UNITS_API, {
                params: {
                    page: currentPage,
                    limit: 10,
                    search: debouncedSearch || undefined
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setUnits(response.data.data || []);
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || response.data.totalPages || 1);
                setTotalRecords(pagination.total || pagination.totalRecords || response.data.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUnits();
    }, [currentPage, debouncedSearch]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const handleDeleteUnit = async (id) => {
        setOpenMenuId(null);
        const result = await Swal.fire({
            title: 'Delete Unit?',
            text: 'Are you sure you want to delete this unit?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-delete-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_UNIT_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Unit has been deleted.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    
                    // Optimistic update
                    setUnits(prev => prev.filter(unit => (unit._id || unit.id) !== id));
                    
                    // If it was the last item on the page and not on page 1, go to previous page
                    if (units.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        fetchUnits();
                    }
                } else {
                    Swal.fire('Error', response.data.error || 'Failed to delete unit', 'error');
                }
            } catch (error) {
                console.error('Error deleting unit:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'An error occurred while deleting unit', 'error');
            }
        }
    };

    return (
        <div className="dashboard">
            <div className="category-header">
                <h2 className="section-title" style={{ margin: 0 }}>Unit Master</h2>
                {hasPermission('UNIT', 'create') && (
                    <button className="add-category-btn" onClick={() => navigate('/unit-master/new')}>
                        <MdAdd size={20} />
                        Add Unit
                    </button>
                )}
            </div>

            <div className="card-container-category">
                {/* Search Bar */}
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <div style={{ position: 'relative', maxWidth: '500px' }}>
                        <MdSearch
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '1.25rem'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search units..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.625rem 1rem 0.625rem 2.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                background: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#e5e7eb',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: '#4b5563',
                                    fontSize: '12px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                {loading ? (
                    <Loader text="Fetching units..." />
                ) : units.length === 0 ? (
                    <NoData text="No units found. Click 'Add Unit' to create one." />
                ) : (
                    <>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Unit Name</th>
                                        <th>Status</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {units.map((unit) => (
                                        <tr key={unit._id || unit.id}>
                                            <td>
                                                <strong>{unit.unitName}</strong>
                                            </td>
                                            <td>
                                                <span
                                                    className={`status-badge ${unit.status === 'active' ? 'active' : 'inactive'}`}
                                                >
                                                    {unit.status}
                                                </span>
                                            </td>
                                            <td className={`action-cell ${openMenuId === (unit._id || unit.id) ? 'menu-open' : ''}`}>
                                                <button
                                                    className="action-menu-btn"
                                                    onClick={(e) => toggleMenu(unit._id || unit.id, e)}
                                                    title="Actions"
                                                >
                                                    <MdMoreVert size={20} />
                                                </button>

                                                <ActionMenuPortal
                                                    isOpen={openMenuId === (unit._id || unit.id)}
                                                    anchorRect={anchorRect}
                                                    onClose={() => setOpenMenuId(null)}
                                                >
                                                    <div className="action-dropdown">
                                                        {hasPermission('UNIT', 'read') && (
                                                            <button className="action-item" onClick={() => navigate(`/unit-master/view/${unit._id || unit.id}`)}>
                                                                <MdVisibility size={16} /> View Details
                                                            </button>
                                                        )}
                                                        {hasPermission('UNIT', 'update') && (
                                                            <button className="action-item" onClick={() => navigate(`/unit-master/edit/${unit._id || unit.id}`)}>
                                                                <MdEdit size={16} /> Edit Unit
                                                            </button>
                                                        )}
                                                        {hasPermission('UNIT', 'delete') && (
                                                            <button
                                                                className="action-item delete"
                                                                onClick={() => handleDeleteUnit(unit._id || unit.id)}
                                                            >
                                                                <MdDelete size={16} /> Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                </ActionMenuPortal>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="mobile-card-view">
                            {units.map((unit) => (
                                <div key={unit._id || unit.id} className="category-card">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <strong>{unit.unitName}</strong>
                                        </div>
                                        <button
                                            className="action-menu-btn"
                                            onClick={(e) => toggleMenu(unit._id || unit.id, e)}
                                            title="Actions"
                                        >
                                            <MdMoreVert size={20} />
                                        </button>
                                        <ActionMenuPortal
                                            isOpen={openMenuId === (unit._id || unit.id)}
                                            anchorRect={anchorRect}
                                            onClose={() => setOpenMenuId(null)}
                                        >
                                            <div className="action-dropdown shadow-lg">
                                                {hasPermission('UNIT', 'read') && (
                                                    <button className="action-item" onClick={() => navigate(`/unit-master/view/${unit._id || unit.id}`)}>
                                                        <MdVisibility size={16} /> View Details
                                                    </button>
                                                )}
                                                {hasPermission('UNIT', 'update') && (
                                                    <button className="action-item" onClick={() => navigate(`/unit-master/edit/${unit._id || unit.id}`)}>
                                                        <MdEdit size={16} /> Edit Unit
                                                    </button>
                                                )}
                                                {hasPermission('UNIT', 'delete') && (
                                                    <button
                                                        className="action-item delete"
                                                        onClick={() => handleDeleteUnit(unit._id || unit.id)}
                                                    >
                                                        <MdDelete size={16} /> Delete
                                                    </button>
                                                )}
                                            </div>
                                        </ActionMenuPortal>
                                    </div>
                                    <div className="card-body">
                                        <div className="card-row">
                                            <span className="card-label">Status:</span>
                                            <span
                                                className={`status-badge ${unit.status === 'active' ? 'active' : 'inactive'}`}
                                            >
                                                {unit.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalRecords={totalRecords}
                            currentCount={units.length}
                            entityName="Units"
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default UnitMaster;
